import React, { useState, useEffect } from 'react';
import { addHours, isAfter } from 'date-fns';
import { safeFormat, validateAndFormatDate } from '@/utils/date-helpers';
import { Calendar, Clock, MapPin, Video, Edit, Info, Send, X, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChatButton } from '@/components/dashboard/shared/ChatButton';
import { TimeChangeRequestModal } from './TimeChangeRequestModal';
import { LateCancellationRequestModal } from './LateCancellationRequestModal';
import { useTranslation } from 'react-i18next';
import type { Appointment } from '@/components/dashboard/client/types/appointmentTypes';
import { SendNoteModal } from '../SendNoteModal';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface LateCancellationRequest {
  id: string;
  status: string;
  client_reason: string | null;
  practitioner_response: string | null;
  responded_at: string | null;
}

interface AppointmentCardProps {
  appointment: Appointment;
  onRequestRefund?: (appointmentId: string) => void;
  isRefundable?: boolean;
  onOpenChat?: () => void;
  unreadCount?: number;
  onTimeChangeRequested?: () => void;
}

export function AppointmentCard({
  appointment,
  onRequestRefund,
  isRefundable = false,
  onOpenChat,
  unreadCount = 0,
  onTimeChangeRequested
}: AppointmentCardProps) {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const { toast } = useToast();
  const [isTimeChangeModalOpen, setIsTimeChangeModalOpen] = useState(false);
  const [isLateCancellationModalOpen, setIsLateCancellationModalOpen] = useState(false);
  const [isSendNoteOpen, setIsSendNoteOpen] = useState(false);
  const [googleMeetLink, setGoogleMeetLink] = useState<string | null>(null);
  const [isLoadingMeetLink, setIsLoadingMeetLink] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [meetLinkError, setMeetLinkError] = useState<string | null>(null);
  const [isCreatingMeetLink, setIsCreatingMeetLink] = useState(false);
  const [lateCancellationRequest, setLateCancellationRequest] = useState<LateCancellationRequest | null>(null);

  // Fetch late cancellation request status for this booking + realtime subscription
  useEffect(() => {
    if (!appointment.id) return;

    const fetchLateCancellationRequest = async () => {
      try {
        const { data, error } = await supabase
          .from('late_cancellation_requests')
          .select('id, status, client_reason, practitioner_response, responded_at')
          .eq('booking_id', appointment.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error fetching late cancellation request:', error);
          return;
        }

        if (data && data.length > 0) {
          setLateCancellationRequest(data[0]);
        }
      } catch (err) {
        console.error('Error fetching late cancellation request:', err);
      }
    };

    fetchLateCancellationRequest();

    // Subscribe to realtime changes so client sees practitioner responses immediately
    const channel = supabase
      .channel(`client-late-cancel-${appointment.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'late_cancellation_requests',
          filter: `booking_id=eq.${appointment.id}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const record = payload.new as LateCancellationRequest;
            setLateCancellationRequest(record);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [appointment.id]);

  // Check for Google Meet link if appointment is virtual/online
  useEffect(() => {
    const fetchGoogleMeetLink = async () => {
      if (appointment.format !== 'online' && appointment.format !== 'virtual') return;
      
      setIsLoadingMeetLink(true);
      setMeetLinkError(null);
      try {
        const { data, error } = await supabase
          .from('google_calendar_meetings')
          .select('meeting_link')
          .eq('booking_id', appointment.id)
          .maybeSingle();
          
        if (!error && data?.meeting_link) {
          setGoogleMeetLink(data.meeting_link);
        } else if (!data) {
          // No meeting found, this might be normal for older appointments
          console.log('No Google Meet link found for appointment:', appointment.id);
        }
      } catch (error) {
        console.error('Error fetching Google Meet link:', error);
        setMeetLinkError('Failed to load meeting link');
      } finally {
        setIsLoadingMeetLink(false);
      }
    };
    
    fetchGoogleMeetLink();
  }, [appointment.id, appointment.format, user?.id]);

  // Removed client-side Google Meet creation - only practitioners can create meetings
  
  // Debug logging and safe date parsing
  console.log('🔔 AppointmentCard - appointment.id:', appointment.id, 'unreadCount:', unreadCount);
  const appointmentDate = React.useMemo(() => {
    return validateAndFormatDate(appointment.start_time) || new Date();
  }, [appointment.start_time, appointment.id]);
  const practitionerName = appointment.practitioner?.first_name && appointment.practitioner?.last_name
    ? `${appointment.practitioner.first_name} ${appointment.practitioner.last_name}`
    : 'Unknown Practitioner';

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
      case 'scheduled':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'reserved':
        return 'bg-orange-100 text-orange-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
      case 'cancelled by client':
      case 'cancelled by practitioner':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Only show request refund for paid bookings (not free) and not already processed
  const canRequestRefund = appointment.refund_status !== 'processed' &&
                          appointment.payment_status !== 'Free' &&
                          appointment.price_at_booking > 0;

  // Check if appointment time can be changed (24 hours before start time and status is 'Scheduled')
  const now = new Date();
  const twentyFourHoursFromNow = addHours(now, 24);
  const isScheduledOrReserved = ['scheduled', 'reserved'].includes(appointment.status.toLowerCase());
  const canChangeTime = appointment.status.toLowerCase() === 'scheduled' && 
                        isAfter(appointmentDate, twentyFourHoursFromNow);
  
  // Check if appointment can be cancelled (allowed for 'Scheduled' and 'Reserved' appointments)
  const canCancel = isScheduledOrReserved;
  
  // Check if this is a late cancellation (within 24 hours) - applies to both Scheduled and Reserved appointments
  const isLateCancellation = isScheduledOrReserved &&
                             !isAfter(appointmentDate, twentyFourHoursFromNow);

  const handleTimeChangeRequested = () => {
    setIsTimeChangeModalOpen(false);
    onTimeChangeRequested?.();
  };

  const handleCancelAppointment = async () => {
    if (!canCancel) return;
    
    // If within 24 hours, open late cancellation request modal
    if (isLateCancellation) {
      setIsLateCancellationModalOpen(true);
      return;
    }

    // Otherwise, proceed with direct cancellation
    setIsCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-booking-by-client', {
        body: { bookingId: appointment.id, reason: 'Cancelled by client' }
      });

      if (error) throw error;

      if (data?.success) {
        if (data?.requiresApproval) {
          // Late cancellation - needs practitioner approval
          toast({
            title: "Cancellation Request Submitted",
            description: "Your cancellation request has been sent to the practitioner for approval. You will be notified of their decision."
          });
        } else {
          toast({
            title: "Cancellation Successful",
            description: data.refundInitiated
              ? "Your appointment has been cancelled and refund has been initiated"
              : "Your appointment has been cancelled"
          });
        }
        // Refresh the page or trigger parent component refresh
        window.location.reload();
      } else {
        throw new Error(data?.error || 'Failed to cancel appointment');
      }
    } catch (error) {
      console.error('Cancel appointment error:', error);
      toast({
        variant: "destructive",
        title: "Cancellation Failed",
        description: error instanceof Error ? error.message : 'Please try again'
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleLateCancellationSubmitted = () => {
    setIsLateCancellationModalOpen(false);
    onTimeChangeRequested?.(); // Trigger refresh
  };

  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-semibold text-lg">{appointment.service?.service_name}</h3>
            <p className="text-gray-600">{practitionerName}</p>
          </div>
          <Badge className={getStatusColor(appointment.status)}>
            {appointment.status}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        <div className="flex items-center text-gray-600">
          <Calendar className="h-4 w-4 mr-2" />
          <span>{safeFormat(appointmentDate, 'EEEE, MMMM d, yyyy')}</span>
        </div>
        
        <div className="flex items-center text-gray-600">
          <Clock className="h-4 w-4 mr-2" />
          <span>{safeFormat(appointmentDate, 'h:mm a')}</span>
        </div>

        {(appointment.format === 'in_person' || appointment.format === 'in-person') && (
          <div className="flex items-start text-gray-600">
            <MapPin className="h-4 w-4 mr-2 mt-0.5" />
            <span className="text-sm">{appointment.location_details || t('inPersonSession', 'In-Person Session')}</span>
          </div>
        )}

        {(appointment.format === 'online' || appointment.format === 'virtual' || (!appointment.format || (appointment.format !== 'in_person' && appointment.format !== 'in-person'))) && (
          <div className="space-y-2">
            <div className="flex items-center text-gray-600">
              <Video className="h-4 w-4 mr-2" />
              <span>Online Session</span>
            </div>
            
            {/* Google Meet Link Section */}
            <div className="ml-6 space-y-2">
              {isLoadingMeetLink ? (
                <div className="text-sm text-gray-500">Loading meeting link...</div>
              ) : googleMeetLink ? (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 h-auto text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    // For mobile, open in same tab to avoid popup blockers
                    if (window.innerWidth < 768) {
                      window.location.href = googleMeetLink;
                    } else {
                      window.open(googleMeetLink, '_blank');
                    }
                  }}
                >
                  Join Google Meet
                </Button>
              ) : meetLinkError ? (
                <div className="text-sm text-gray-500">
                  Your practitioner will create the meeting link for this session.
                </div>
              ) : appointment.status.toLowerCase() === 'scheduled' ? (
                <div className="text-sm text-gray-500">
                  Waiting for practitioner to create meeting link...
                </div>
              ) : (
                <div className="text-sm text-gray-500">No meeting link available</div>
              )}
            </div>
          </div>
        )}

        {appointment.session_instructions && (
          <div className="flex items-start text-gray-700 bg-gray-50 p-3 rounded-md border border-gray-200">
            <Info className="h-4 w-4 mr-2 mt-0.5 text-gray-600" />
            <span className="text-sm whitespace-pre-wrap">{appointment.session_instructions}</span>
          </div>
        )}

        {/* Late Cancellation Request Status */}
        {lateCancellationRequest && (
          <div className={`rounded-md border p-3 text-sm ${
            lateCancellationRequest.status === 'rejected'
              ? 'border-red-200 bg-red-50'
              : lateCancellationRequest.status === 'approved'
                ? 'border-green-200 bg-green-50'
                : 'border-yellow-200 bg-yellow-50'
          }`}>
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle className={`h-4 w-4 ${
                lateCancellationRequest.status === 'rejected' ? 'text-red-600'
                  : lateCancellationRequest.status === 'approved' ? 'text-green-600'
                    : 'text-yellow-600'
              }`} />
              <span className="font-medium">
                {lateCancellationRequest.status === 'rejected'
                  ? 'Cancellation Request Rejected'
                  : lateCancellationRequest.status === 'approved'
                    ? 'Cancellation Request Approved'
                    : 'Cancellation Request Pending'}
              </span>
            </div>
            {lateCancellationRequest.practitioner_response && (
              <div className="mt-2 ml-6">
                <p className="text-gray-500 text-xs mb-1">Practitioner's Response:</p>
                <p className="text-gray-700 whitespace-pre-wrap">{lateCancellationRequest.practitioner_response}</p>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between pt-3">
          <div className="flex-1">
            {/* 2x2 Grid layout for action buttons */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {/* Row 1: Chat | Send Note */}
              <ChatButton
                bookingId={appointment.id}
                className="flex items-center justify-center gap-2 text-sm"
                currentUserType="client"
                otherParticipantName={practitionerName}
                unreadCount={unreadCount}
              />
              
              <Button
                variant="outline"
                size="sm"
                className="flex items-center justify-center gap-1 text-sm"
                onClick={() => setIsSendNoteOpen(true)}
              >
                <Send className="h-4 w-4" />
                <span className="hidden sm:inline">Send Note</span>
                <span className="sm:hidden">Note</span>
              </Button>
              
              {/* Row 2: Reschedule | Cancel Appointment */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsTimeChangeModalOpen(true)}
                disabled={!canChangeTime}
                className={`flex items-center justify-center gap-1 text-sm ${
                  canChangeTime 
                    ? 'text-blue-600 border-blue-200 hover:bg-blue-50' 
                    : 'opacity-50 cursor-not-allowed'
                }`}
              >
                <Edit className="h-4 w-4" />
                <span className="hidden sm:inline">Reschedule</span>
                <span className="sm:hidden">Time</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelAppointment}
                disabled={!canCancel || isCancelling || !!lateCancellationRequest}
                className={`flex items-center justify-center gap-1 text-sm ${
                  canCancel && !lateCancellationRequest
                    ? 'text-red-600 border-red-200 hover:bg-red-50'
                    : 'opacity-50 cursor-not-allowed'
                }`}
              >
                <X className="h-4 w-4" />
                <span className="hidden sm:inline">
                  {isCancelling ? 'Processing...'
                    : lateCancellationRequest?.status === 'pending' ? 'Cancel Pending'
                    : lateCancellationRequest?.status === 'rejected' ? 'Request Rejected'
                    : isLateCancellation ? 'Request Cancel'
                    : 'Cancel'}
                </span>
                <span className="sm:hidden">
                  {isCancelling ? '...'
                    : lateCancellationRequest?.status === 'pending' ? 'Pending'
                    : lateCancellationRequest?.status === 'rejected' ? 'Rejected'
                    : 'Cancel'}
                </span>
              </Button>
            </div>
          </div>
          
          <div className="text-right">
            <p className="font-semibold">${appointment.price_at_booking}</p>
            <p className="text-sm text-gray-500">{appointment.currency_at_booking}</p>
          </div>
        </div>
      </CardContent>
      
      {/* Time Change Request Modal */}
      <TimeChangeRequestModal
        appointment={appointment}
        isOpen={isTimeChangeModalOpen}
        onClose={() => setIsTimeChangeModalOpen(false)}
        onRequestSubmitted={handleTimeChangeRequested}
      />

      {/* Late Cancellation Request Modal */}
      <LateCancellationRequestModal
        appointment={appointment}
        isOpen={isLateCancellationModalOpen}
        onClose={() => setIsLateCancellationModalOpen(false)}
        onRequestSubmitted={handleLateCancellationSubmitted}
      />
      
      {/* Send Note Modal */}
      <SendNoteModal
        isOpen={isSendNoteOpen}
        onClose={() => setIsSendNoteOpen(false)}
        bookingId={appointment.id}
        practitionerName={practitionerName}
      />
    </Card>
  );
}
